import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from 'types';

import { apiBaseQuery } from 'modules/apiBaseQuery';

import {
  FetchDashboardResponse,
  IDashboardChartPayload,
  IDashboardExchangeChartResponse,
  IDashboardPredictionsStaticChartDataResponse,
  IDashboardRevenueExpenseLineChartResponse,
  IDashboardRevenueExpenseStaticChartResponse,
  IDashboardStaticForwardChartResponse,
  IDashboardStaticSpotChartResponse,
  IDashboardVolumeChartResponse,
} from '../types/eecs-dashboard.types';

export const eecsDashboardApi = createApi({
  reducerPath: 'eecsDashboardApi',
  baseQuery: apiBaseQuery(`${process.env.REACT_APP_API_URL}/v2/api/admin/necs`),
  endpoints: (builder) => ({
    fetchDashboard: builder.query<ApiResponse<FetchDashboardResponse>, any>({
      query: () => ({
        url: `/dashboard`,
        method: 'GET',
      }),
    }),

    fetchSpotVolumeChart: builder.query<ApiResponse<IDashboardVolumeChartResponse>, IDashboardChartPayload>({
      query: (params) => ({
        url: `/charts/go-certificates`,
        method: 'GET',
        params,
      }),
    }),

    fetchSpotExchangeChart: builder.query<ApiResponse<IDashboardExchangeChartResponse>, IDashboardChartPayload>({
      query: (params) => ({
        url: `/charts/go-certificates-exchange`,
        method: 'GET',
        params,
      }),
    }),

    fetchSpotStaticChart: builder.query<ApiResponse<IDashboardStaticSpotChartResponse>, any>({
      query: () => ({
        url: `/charts/go-certificates-by-technology`,
        method: 'GET',
      }),
    }),

    fetchForwardVolumeChart: builder.query<ApiResponse<IDashboardVolumeChartResponse>, IDashboardChartPayload>({
      query: (params) => ({
        url: `/charts/go-forwards`,
        method: 'GET',
        params,
      }),
    }),

    fetchForwardExchangeChart: builder.query<ApiResponse<IDashboardExchangeChartResponse>, IDashboardChartPayload>({
      query: (params) => ({
        url: `/charts/go-forwards-exchange`,
        method: 'GET',
        params,
      }),
    }),

    fetchForwardStaticChart: builder.query<ApiResponse<IDashboardStaticForwardChartResponse>, any>({
      query: () => ({
        url: `/charts/go-forwards-by-technology`,
        method: 'GET',
      }),
    }),

    fetchRevenueExpenseLineChart: builder.query<
      ApiResponse<IDashboardRevenueExpenseLineChartResponse>,
      IDashboardChartPayload
    >({
      query: (params) => ({
        url: `/charts/cash-flow`,
        method: 'GET',
        params,
      }),
    }),

    fetchRevenueExpenseStaticChart: builder.query<ApiResponse<IDashboardRevenueExpenseStaticChartResponse>, any>({
      query: () => ({
        url: `/charts/cash-flow-by-type`,
        method: 'GET',
      }),
    }),

    fetchPredictionYears: builder.query<ApiResponse<string[]>, any>({
      query: () => ({
        url: `/charts/production-predictions-years`,
        method: 'GET',
      }),
    }),

    fetchPredictionVolumeChart: builder.query<ApiResponse<IDashboardVolumeChartResponse>, IDashboardChartPayload>({
      query: (params) => ({
        url: `/charts/production-predictions-line-chart`,
        method: 'GET',
        params,
      }),
    }),
    fetchPredictionStaticChart: builder.query<
      ApiResponse<IDashboardPredictionsStaticChartDataResponse>,
      IDashboardChartPayload
    >({
      query: (params) => ({
        url: `/charts/production-predictions-pie-chart`,
        method: 'GET',
        params,
      }),
    }),
  }),
});

export const {
  useLazyFetchDashboardQuery,
  useLazyFetchSpotExchangeChartQuery,
  useLazyFetchSpotVolumeChartQuery,
  useLazyFetchSpotStaticChartQuery,
  useLazyFetchForwardVolumeChartQuery,
  useLazyFetchForwardExchangeChartQuery,
  useLazyFetchForwardStaticChartQuery,
  useLazyFetchRevenueExpenseLineChartQuery,
  useLazyFetchRevenueExpenseStaticChartQuery,
  useLazyFetchPredictionYearsQuery,
  useLazyFetchPredictionVolumeChartQuery,
  useLazyFetchPredictionStaticChartQuery,
} = eecsDashboardApi;
